import { useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, User, Mail, Phone, MapPin, Dog, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Footer from "@/components/sections/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PetPantryPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    pet_type: "",
    pet_name: "",
    needs: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/pet-pantry/request`, form);
      toast.success("Your request has been submitted!");
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const update = (field, value) => setForm({ ...form, [field]: value });

  if (submitted) {
    return (
      <main data-testid="pet-pantry-success" className="min-h-screen">
        <div className="pt-28 pb-24 md:pt-36 md:pb-32 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center max-w-md px-6"
          >
            <ShoppingBag className="w-16 h-16 text-[#8FA396] mx-auto mb-6" />
            <h1 className="font-heading text-3xl sm:text-4xl text-[#2C2C2A] mb-4">
              Request Received!
            </h1>
            <p className="font-body text-base text-[#6C6B66] mb-6">
              We've received your Pet Pantry request. Our team will reach out to you soon
              to coordinate how we can help your fur baby.
            </p>
            <p className="font-body text-sm text-[#8FA396] italic">
              "No pet should go hungry. No parent should feel helpless."
            </p>
          </motion.div>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main data-testid="pet-pantry-page" className="min-h-screen">
      <div className="pt-28 pb-24 md:pt-36 md:pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <p className="font-body text-xs font-bold tracking-[0.2em] uppercase text-[#C0603A] mb-4">
              Pet Pantry Program
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-none text-[#2C2C2A] font-medium mb-4">
              Request Assistance
            </h1>
            <p className="font-body text-base sm:text-lg text-[#6C6B66] max-w-xl">
              If you're a fur parent in need of food, treats, blankets, or kennel supplies
              for your pet, fill out this form. We're here to help &mdash; no judgment, just love.
            </p>
          </motion.div>

          {/* Info Banner */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-[#8FA396]/10 border border-[#8FA396]/20 rounded-3xl p-6 mb-10"
          >
            <p className="font-body text-sm text-[#3A3A38]">
              <strong>Available resources:</strong> Pet food (dog & cat), treats, blankets,
              kennel supplies, grooming services, and referrals to affordable veterinary care.
            </p>
          </motion.div>

          {/* Form */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25 }}
            className="space-y-5"
            data-testid="pet-pantry-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
                <input
                  type="text"
                  placeholder="Your Name *"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                  data-testid="pantry-name-input"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
                <input
                  type="email"
                  placeholder="Email Address *"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                  data-testid="pantry-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                  data-testid="pantry-phone-input"
                />
              </div>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
                <input
                  type="text"
                  placeholder="Your City / Area"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                  data-testid="pantry-address-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="relative">
                <Dog className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6C6B66]" />
                <input
                  type="text"
                  placeholder="Pet Type (Dog, Cat, etc.)"
                  value={form.pet_type}
                  onChange={(e) => update("pet_type", e.target.value)}
                  className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                  data-testid="pantry-pet-type-input"
                />
              </div>
              <input
                type="text"
                placeholder="Pet's Name"
                value={form.pet_name}
                onChange={(e) => update("pet_name", e.target.value)}
                className="w-full bg-white border border-[#E8E3DD] rounded-xl px-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow"
                data-testid="pantry-pet-name-input"
              />
            </div>

            <select
              value={form.needs}
              onChange={(e) => update("needs", e.target.value)}
              className="w-full bg-white border border-[#E8E3DD] rounded-xl px-4 py-3.5 font-body text-sm text-[#3A3A38] focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow appearance-none"
              data-testid="pantry-needs-select"
            >
              <option value="">What do you need most?</option>
              <option value="food">Pet Food</option>
              <option value="treats">Treats</option>
              <option value="blankets">Blankets</option>
              <option value="kennel_supplies">Kennel Supplies</option>
              <option value="grooming">Grooming Services</option>
              <option value="vet_resources">Veterinary Resources</option>
              <option value="multiple">Multiple Items</option>
            </select>

            <div className="relative">
              <MessageSquare className="absolute left-4 top-4 w-4 h-4 text-[#6C6B66]" />
              <textarea
                placeholder="Tell us more about your situation (optional)"
                rows={4}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="w-full bg-white border border-[#E8E3DD] rounded-xl pl-11 pr-4 py-3.5 font-body text-sm text-[#3A3A38] placeholder:text-[#6C6B66]/60 focus:outline-none focus:ring-2 focus:ring-[#C0603A]/40 focus:border-[#C0603A] transition-shadow resize-none"
                data-testid="pantry-message-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="pantry-submit-button"
              className="bg-[#C0603A] text-white hover:bg-[#A64D29] disabled:opacity-60 transition-all duration-300 rounded-full px-8 py-4 font-body font-semibold text-base inline-flex items-center gap-2 hover:shadow-[0_12px_48px_rgba(192,96,58,0.2)]"
            >
              <Send className="w-4 h-4" />
              {loading ? "Submitting..." : "Submit Request"}
            </button>
          </motion.form>
        </div>
      </div>
      <Footer />
    </main>
  );
}
